// Availability contracts (AVL-*).
//
// AVAILABILITY IS NEVER CONSENT (invariant 3, AVL-019). Nothing in this module
// creates a commitment, and nothing here accepts anything. It records "I could help
// then" — an offer of possibility. The only way coverage moves is an explicit
// acceptance, which lives in ./commitments.ts.
//
// Matching is CONTAINMENT, not overlap (AVL-021): an offer must fully contain the
// need's window. Someone free 09:00–11:00 is not a match for a need running
// 09:15–13:00, however much of it they cover. There is no overlap percentage and no
// "best match" ranking anywhere in this contract, deliberately.

import { z } from "zod";

import type { AvailabilityRejection, Result } from "../errors.js";
import {
  availabilityBatchIdSchema,
  circleIdSchema,
  helpModeSchema,
  instantSchema,
  personIdSchema,
  supportNeedIdSchema,
} from "../primitives.js";

const availabilityWindowInput = z
  .object({
    startsAt: instantSchema,
    endsAt: instantSchema,
    helpModes: z.array(helpModeSchema).min(1, "Choose at least one kind of help."),
    note: z.string().max(500).optional(),
  })
  .refine((w) => w.endsAt > w.startsAt, {
    message: "An availability window must end after it starts.",
    path: ["endsAt"],
  });

export const saveAvailabilityInput = z.object({
  circleId: circleIdSchema,
  /**
   * Whose availability this is. When it differs from the caller, this is a PROXY
   * entry and requires recorded consent (AVL-015) — the boundary rejects it with
   * `proxy_consent_missing` rather than accepting it quietly.
   */
  personId: personIdSchema,
  /** Submitted as a batch so a week's worth of windows is one atomic change. */
  windows: z.array(availabilityWindowInput).min(1, "Add at least one time."),
  /**
   * Present when the client had an unsent local draft (PLT-011 — the single
   * exception to fail-closed offline). Lets the server recognise a resubmission
   * instead of duplicating the batch.
   */
  draftKey: z.string().max(128).optional(),
});

export const withdrawAvailabilityInput = z.object({
  circleId: circleIdSchema,
  availabilityBatchId: availabilityBatchIdSchema,
});

/** Who could cover this need. Containment-filtered; never ranked by overlap. */
export const matchesForNeedInput = z.object({
  circleId: circleIdSchema,
  supportNeedId: supportNeedIdSchema,
});

export type SaveAvailabilityInput = z.input<typeof saveAvailabilityInput>;
export type WithdrawAvailabilityInput = z.input<typeof withdrawAvailabilityInput>;
export type MatchesForNeedInput = z.input<typeof matchesForNeedInput>;

/**
 * A candidate helper.
 *
 * Carries no score. The list is "these people said they could be free for the whole
 * window and can do this kind of help" — an Organiser then ASKS one, which is still
 * not coverage until accepted (invariant 4).
 */
export interface MatchCandidate {
  readonly personId: string;
  readonly displayName: string;
  readonly availabilityWindowId: string;
  /** Whether the person recorded this availability or it was recorded for them. */
  readonly source: "self" | "proxy";
  /** When the matching availability was last recorded, for freshness ordering. */
  readonly recordedAt: number;
  /** Present for proxy-recorded availability so attribution remains visible. */
  readonly recordedByPersonId?: string;
  /** How the proxy availability was relayed; present only when it was recorded. */
  readonly sourceChannel?: string;
  /** Why someone is NOT offered, when the UI shows exclusions (AVL-022). */
  readonly ineligibility?: string;
}

export interface AvailabilitySaved {
  readonly availabilityBatchId: string;
  readonly windowCount: number;
}

export type SaveAvailabilityResult = Result<AvailabilitySaved, AvailabilityRejection>;
export type MatchesForNeedResult = Result<readonly MatchCandidate[], AvailabilityRejection>;
